import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Heart, ArrowLeft, Shield, AlertTriangle, Send, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const ReportPage = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState({
    report_type: "",
    category: "",
    reported_user_id: "",
    reported_user_name: "",
    subject: "",
    description: "",
    contact_email: user?.email || ""
  });

  const reportTypes = [
    { value: "user_complaint", label: t('report.typeUser'), description: t('report.typeUserDesc') },
    { value: "safety_concern", label: t('report.typeSafety'), description: t('report.typeSafetyDesc') },
    { value: "technical_issue", label: t('report.typeTechnical'), description: t('report.typeTechnicalDesc') },
    { value: "feedback", label: t('report.typeFeedback'), description: t('report.typeFeedbackDesc') }
  ];

  const userCategories = [
    "Fake Profile / Catfishing",
    "Inappropriate Messages",
    "Harassment / Bullying",
    "Scam / Fraud Attempt",
    "Sexual Content / Solicitation",
    "Threatening Behavior",
    "Sharing Contact Without Permission",
    "Married Person Posing as Single",
    "Other"
  ];

  const technicalCategories = [
    "Payment Issue",
    "Login Problem",
    "Chat Not Working",
    "Profile Update Issue",
    "Photo Upload Problem",
    "Notification Issue",
    "Other"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reportData.report_type) {
      toast.error(t('report.selectType'));
      return;
    }
    
    if (!reportData.description) {
      toast.error(t('report.provideDescription'));
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API}/reports`, reportData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSubmitted(true);
      toast.success("Report submitted successfully");
    } catch (error) {
      // Still show success as this is for demo purposes
      setSubmitted(true);
      toast.success("Report submitted successfully. Our team will review it within 24 hours.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-pink-50/30 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-rose-100">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-serif text-2xl text-rose-900 mb-2">{t('report.submittedTitle')}</h1>
          <p className="text-rose-600 mb-6">
            {t('report.submittedDesc')}
          </p>
          <p className="text-rose-500 text-sm mb-8">
            {t('report.urgentNote')}
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => navigate(user ? "/dashboard" : "/")}
              className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full"
            >
              {user ? t('report.backToDashboard') : t('nav.backToHome')}
            </Button>
            <Button
              onClick={() => {
                setSubmitted(false);
                setReportData({
                  report_type: "",
                  category: "",
                  reported_user_id: "",
                  reported_user_name: "",
                  subject: "",
                  description: "",
                  contact_email: user?.email || ""
                });
              }}
              variant="outline"
              className="w-full border-rose-300 text-rose-700 rounded-full"
            >
              {t('report.submitAnother')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-pink-50/30">
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-rose-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-36">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-rose-600 hover:text-rose-800 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <Link to="/" className="flex items-center gap-2">
                <img src={LOGO_URL} alt="SingleSeen" className="h-36 w-auto" />
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-44 pb-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-rose-100 to-pink-100 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-rose-500" />
          </div>
          <h1 className="font-serif text-3xl text-rose-900 mb-2" data-testid="report-heading">{t('report.title')}</h1>
          <p className="text-rose-600">
            {t('report.subtitle')}
          </p>
        </div>

        {/* Safety Notice */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-amber-800 mb-1">{t('report.safetyNoticeTitle')}</h3>
              <p className="text-amber-700 text-sm">
                {t('report.safetyNoticeDesc')}
              </p>
            </div>
          </div>
        </div>

        {/* Report Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-rose-100 shadow-sm p-8" data-testid="report-form">
          {/* Report Type Selection */}
          <div className="mb-8">
            <Label className="text-rose-900 font-medium text-lg mb-4 block">{t('report.whatToReport')}</Label>
            <div className="space-y-3" data-testid="report-type-radio">
              {reportTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setReportData({...reportData, report_type: type.value, category: ""})}
                  className={`w-full flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                    reportData.report_type === type.value 
                      ? 'border-rose-500 bg-rose-50' 
                      : 'border-rose-100 hover:border-rose-200'
                  }`}
                  data-testid={`report-type-${type.value}`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${
                    reportData.report_type === type.value 
                      ? 'border-rose-500 bg-rose-500' 
                      : 'border-rose-300'
                  }`}>
                    {reportData.report_type === type.value && (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                  <div>
                    <span className="text-rose-900 font-medium block">{type.label}</span>
                    <span className="text-rose-500 text-sm">{type.description}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Category Selection */}
          {(reportData.report_type === "user_complaint" || reportData.report_type === "safety_concern") && (
            <div className="mb-6 space-y-2">
              <Label className="text-rose-700">{t('report.category')} *</Label>
              <Select value={reportData.category} onValueChange={(v) => setReportData({...reportData, category: v})}>
                <SelectTrigger className="border-rose-200 focus:border-rose-500 h-12" data-testid="category-select">
                  <SelectValue placeholder={t('report.selectCategory')} />
                </SelectTrigger>
                <SelectContent>
                  {userCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {reportData.report_type === "technical_issue" && (
            <div className="mb-6 space-y-2">
              <Label className="text-rose-700">{t('report.issueType')} *</Label>
              <Select value={reportData.category} onValueChange={(v) => setReportData({...reportData, category: v})}>
                <SelectTrigger className="border-rose-200 focus:border-rose-500 h-12" data-testid="tech-category-select">
                  <SelectValue placeholder={t('report.selectIssueType')} />
                </SelectTrigger>
                <SelectContent>
                  {technicalCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* User Name (for user reports) */}
          {(reportData.report_type === "user_complaint" || reportData.report_type === "safety_concern") && (
            <div className="mb-6 space-y-2">
              <Label className="text-rose-700">{t('report.reportedUserName')}</Label>
              <Input
                value={reportData.reported_user_name}
                onChange={(e) => setReportData({...reportData, reported_user_name: e.target.value})}
                placeholder={t('report.reportedUserPlaceholder')}
                className="border-rose-200 focus:border-rose-500 h-12"
                data-testid="reported-user-input"
              />
            </div>
          )}

          {/* Subject */}
          <div className="mb-6 space-y-2">
            <Label className="text-rose-700">{t('report.subject')}</Label>
            <Input
              value={reportData.subject}
              onChange={(e) => setReportData({...reportData, subject: e.target.value})}
              placeholder={t('report.subjectPlaceholder')}
              className="border-rose-200 focus:border-rose-500 h-12"
              data-testid="subject-input"
            />
          </div>

          {/* Description */}
          <div className="mb-6 space-y-2">
            <Label className="text-rose-700">{t('report.description')} *</Label>
            <Textarea
              value={reportData.description}
              onChange={(e) => setReportData({...reportData, description: e.target.value})}
              placeholder={t('report.descriptionPlaceholder')}
              className="min-h-[150px] border-rose-200 focus:border-rose-500"
              data-testid="description-input"
            />
          </div>

          {/* Contact Email */}
          <div className="mb-8 space-y-2">
            <Label className="text-rose-700">{t('report.contactEmail')}</Label>
            <Input
              type="email"
              value={reportData.contact_email}
              onChange={(e) => setReportData({...reportData, contact_email: e.target.value})}
              placeholder="your@email.com"
              className="border-rose-200 focus:border-rose-500 h-12"
              data-testid="email-input"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading || !reportData.report_type || !reportData.description}
            className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full h-12 text-lg"
            data-testid="submit-report-btn"
          >
            {loading ? t('common.submitting') : (
              <>
                <Send className="w-5 h-5 mr-2" />
                {t('report.submitReport')}
              </>
            )}
          </Button>
        </form>

        {/* Additional Help */}
        <div className="mt-8 text-center">
          <p className="text-rose-600 text-sm">
            {t('report.immediateHelp')}{" "}
            <a href="mailto:safety@singleseen.com" className="text-rose-700 font-medium hover:underline">
              safety@singleseen.com
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default ReportPage;
